import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { addCommasToNumber, GraviButton, GraviGrid, onExport, Texto } from '@gravitate-js/excalibrr'
import { useAnalyticsReporting } from '@api/useAnalyticsReporting'
import { useUserSettings } from '@hooks/settings'
import moment from 'moment/moment'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { FourWeekAvgCellRenderer } from '@components/AnalyticsReporting/Forecast/FourWeekAvgCellRenderer'
import { defaultDateDef, defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import queryString from 'query-string'
import type { DayRange } from '@/hooks/useDateRangeFilter'
import { DateRangeFilter } from '@/components/_SiteWide/DateRangeFilter'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'analytics-reporting-forecast'

export const Forecast = () => {
  const { useForecastOverview } = useAnalyticsReporting()
  const { updateSetting, getSetting } = useUserSettings()
  const gridAPIRef = useRef(null)
  const { value: dayRange, setValue: setLocalDayRange } = useLocalStorage('analytics-reporting-forecast-dates', {})

  const setDayRange = async (range: DayRange) => {
    setLocalDayRange(range)
    await updateSetting('analytics-reporting-forecast-dates', range)
  }

  const [filters, setFilters] = useState({
    From: moment().add(1, 'days').toDate(),
    To: moment().add(16, 'days').toDate(),
  })

  const { data, status } = useForecastOverview({
    start_date: moment(filters.From).format('YYYY-MM-DDT00:00:00'),
    end_date: moment(filters.To).format('YYYY-MM-DDT00:00:00'),
  })

  useEffect(() => {
    getSetting('analytics-reporting-forecast-dates').then((res) => {
      if (res?.To && res?.From) {
        setFilters({
          To: moment(res.To).toDate(),
          From: moment(res.From).toDate(),
        })
      } else {
        setFilters({
          To: moment().add(16, 'days').toDate(),
          From: moment().add(1, 'days').toDate(),
        })
      }
    })
  }, [])

  const [eventId, setEventId] = useState(null)

  const filteredRowData = useMemo(() => {
    if (eventId) {
      return data?.filter((row) => row.event_id === eventId)
    }
    return data
  }, [eventId, data])

  useEffect(() => {
    setEventId(queryString.parse(window.location.search).event_id)
  }, [])

  const clearEventFilter = () => {
    setEventId(null)
  }

  const columnDefs = useMemo(() => getColumnDefs(), [])
  const getRowId = useCallback((row) => row?.data?.key, [])
  const agPropOverrides = useMemo(() => ({ getRowId }), [getRowId])

  return (
    <GraviGrid
      loading={status === 'pending'}
      externalRef={gridAPIRef}
      columnDefs={columnDefs}
      rowData={filteredRowData || data}
      storageKey={STORAGE_KEY}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      controlBarProps={{
        title: 'Forecast',
        // hiddenFilterKeys: ['event_id'],
        actionButtons: (
          <ActionButtons
            gridAPIRef={gridAPIRef}
            filters={filters}
            setFilters={setFilters}
            setDayRange={setDayRange}
            dayRangeStorage={dayRange}
            clearEventFilter={clearEventFilter}
            currentEventFilter={!!eventId}
          />
        ),
      }}
      agPropOverrides={agPropOverrides}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

export const ActionButtons = ({
  gridAPIRef,
  filters,
  setFilters,
  setDayRange,
  dayRangeStorage,
  clearEventFilter,
  currentEventFilter,
}) => {
  const excelOptions = [
    {
      title: 'Download Forecast Report',
      onClick: () =>
        onExport(gridAPIRef, {
          fileName: `ForecastAnalyticsReport${moment().format('YYYY-MM-DD')}.xlsx`,
          sheetName: 'Forecast Report',
          processCellCallback(params) {
            if (params.value && params.column.colId === 'date') {
              return formatters.DATE(moment(params.value))
            }
            return params.value
          },
        }),
    },
  ]

  return (
    <>
      {currentEventFilter && (
        <GraviButton theme1 className='square-ish mr-3' buttonText='Clear Event Filter' onClick={clearEventFilter} />
      )}

      <UploadDownloadButtons excelOptions={excelOptions} />

      <DateRangeFilter
        min={1}
        max={16}
        currentFilters={filters}
        setCurrentFilters={setFilters}
        getFromStorage={false}
        dayRangeStorage={dayRangeStorage}
        setDayRangeStorage={setDayRange}
        dateRangeVariation
      />
    </>
  )
}

export const getColumnDefs = () => [
  { headerName: 'Product', field: 'product' },
  {
    headerName: 'Site',
    field: 'site',
    comparator: (a, b) => a - b,
  },
  { headerName: 'Tank', field: 'tank' },
  { headerName: 'Date', field: 'date', ...defaultDateDef('date', formatters.DATE) },
  { headerName: 'Market', field: 'market' },
  { headerName: 'Sector', field: 'sector' },
  {
    headerName: 'Customer',
    field: 'customer',
    valueGetter: (params) => params?.data?.customer || '',
  },
  {
    headerName: 'Forecast',
    field: 'forecast',
    ...defaultNumberDef('forecast', 0),
    valueFormatter: (params) => (params?.data?.forecast !== null ? addCommasToNumber(params?.data?.forecast, 0) : ''),
    valueGetter: (params) => params?.data?.forecast || -Infinity,
  },
  {
    headerName: '4-Week Average Sales',
    field: 'sales_avg',
    ...defaultNumberDef('sales_avg', 0),
    cellRenderer: (params) => <FourWeekAvgCellRenderer data={params.data} />,
    valueGetter: (params) => params?.data?.sales_avg || -Infinity,
  },
  { headerName: 'Overridden User', field: 'overridden_user' },
  {
    headerName: 'Original Value',
    field: 'original_value',
    ...defaultNumberDef('original_value', 0),
    valueFormatter: (params) =>
      params?.data?.original_value !== null ? addCommasToNumber(params?.data?.original_value, 0) : '',
    valueGetter: (params) => params?.data?.original_value || -Infinity,
  },
  {
    headerName: 'Event Name',
    field: 'event_name',
  },
]
